import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InlineNotification, Loading, Tile } from "@carbon/react";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import ClosedBiddingNotice from "../../components/ClosedBiddingNotice/ClosedBiddingNotice";
import { isOrderClosedError } from "../../utils/biddingTime";
import {
  useGetInvitationDetails,
  useSubmitInvitationOffer,
} from "../../hooks/useOrderInvitations";
import {
  OrderInvitationOfferRequest,
  OrderInvitationOfferResponse,
} from "../../types";
import {
  makeAnonymousOfferSchema,
  AnonymousOfferFormValues,
} from "./offerSchema";
import {
  allLinesPriced,
  LinePriceInputs,
  linePriceInputsOf,
  linePricesPayload,
  linePricesTotal,
  offerCurrencyOf,
} from "../../utils";
import OrderInfoPanel from "./sections/OrderInfoPanel";
import OfferForm from "./sections/OfferForm";
import "./anonymousOffer.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";

/** All fields defined (empty string when absent) so the Controller-bound Carbon inputs stay
 * controlled; numeric fields are coerced by the Yup schema on submit. Mirrors the pre-Carbon
 * prefill (offer fields from an existing offer; supplier fields blank). */
const buildDefaults = (
  existingOffer?: AnonymousOfferFormValues
): AnonymousOfferFormValues =>
  ({
    email: "",
    taxId: "",
    companyName: "",
    price: existingOffer?.price ?? "",
    quantity: existingOffer?.quantity ?? "",
    discount: existingOffer?.discount ?? "",
    deliveryCost: existingOffer?.deliveryCost ?? "",
    deliveryDate: existingOffer?.deliveryDate ?? "",
    expireDate: existingOffer?.expireDate ?? "",
    offerValidity: existingOffer?.offerValidity ?? "",
    paymentTerm: existingOffer?.paymentTerm ?? "",
    paymentMethod: existingOffer?.paymentMethod ?? "",
    leadTime: existingOffer?.leadTime ?? "",
    currency: existingOffer?.currency ?? "",
    deliveryFormat: existingOffer?.deliveryFormat ?? "",
    comment: existingOffer?.comment ?? "",
    generalComments: existingOffer?.generalComments ?? "",
    pickupAddress: existingOffer?.pickupAddress ?? "",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any;

/**
 * Anonymous supplier offer entry (`/order-invitations/:token`), rebuilt on Carbon + `AuthLayout`.
 * Shows the order-invitation summary (`OrderInfoPanel`) and an offer form (`OfferForm`) an
 * unauthenticated supplier submits via the tokenised link. The `POST /order-invitations/offer`
 * lifecycle stays owned by the Offers flow — this migrates only the FE screen.
 */
const AnonymousOffer: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [submitResult, setSubmitResult] =
    useState<OrderInvitationOfferResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetInvitationDetails(token);
  const existingOffer = data?.existingOffer as
    | AnonymousOfferFormValues
    | undefined;

  const { control, handleSubmit, setValue } = useForm<AnonymousOfferFormValues>(
    {
      resolver: yupResolver(
        makeAnonymousOfferSchema(Boolean(data?.buyerPicksUp))
      ),
      defaultValues: buildDefaults(existingOffer),
    }
  );

  // SCRUM-116: a service quoted by line is priced line by line, each line in its own currency;
  // the offer's price and currency follow its lines.
  const pricedLines = useMemo(
    () => (data?.serviceToQuote?.lines ?? []).filter((line) => !!line.code),
    [data?.serviceToQuote]
  );
  const [linePrices, setLinePrices] = useState<LinePriceInputs>({});
  const [linePricesTouched, setLinePricesTouched] = useState(false);
  useEffect(() => {
    setLinePrices(
      linePriceInputsOf(
        pricedLines,
        data?.existingOffer?.linePrices,
        data?.existingOffer?.currency || data?.currency || "USD"
      )
    );
  }, [pricedLines, data?.existingOffer, data?.currency]);

  const changeLines = (next: LinePriceInputs) => {
    setLinePrices(next);
    setLinePricesTouched(true);
    const total = linePricesTotal(pricedLines, next);
    setValue("currency", offerCurrencyOf(pricedLines, next));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue("price", (total > 0 ? total : "") as any, {
      shouldValidate: true,
    });
  };
  const changeLinePrice = (code: string, unitPrice: string) =>
    changeLines({ ...linePrices, [code]: { ...linePrices[code], unitPrice } });
  const changeLineCurrency = (code: string, currency: string) =>
    changeLines({ ...linePrices, [code]: { ...linePrices[code], currency } });

  const { mutate: submitOffer, isPending } = useSubmitInvitationOffer(
    (res) => {
      setSubmitResult(res);
      setSubmitError(null);
    },
    (error) =>
      setSubmitError(
        isOrderClosedError(error) ? "offer.orderClosed" : "anonymousOffer.error"
      )
  );

  const onSubmit = (values: AnonymousOfferFormValues) => {
    if (!data) return;
    if (pricedLines.length > 0 && !allLinesPriced(pricedLines, linePrices)) {
      setLinePricesTouched(true);
      setSubmitError("linePricing.missingAll");
      return;
    }
    const body: OrderInvitationOfferRequest = {
      invitationId: data.invitationId,
      email: values.email,
      taxId: values.taxId,
      companyName: values.companyName,
      price: values.price,
      quantity: values.quantity,
      discount: values.discount,
      ...(data.buyerPicksUp
        ? { pickupAddress: values.pickupAddress }
        : { deliveryCost: values.deliveryCost }),
      deliveryDate: values.deliveryDate,
      expireDate: values.expireDate,
      offerValidity: values.offerValidity,
      paymentTerm: values.paymentTerm,
      paymentMethod: values.paymentMethod,
      leadTime: values.leadTime,
      currency: values.currency,
      deliveryFormat: values.deliveryFormat,
      comment: values.comment,
      generalComments: values.generalComments,
      ...(pricedLines.length > 0
        ? { linePrices: linePricesPayload(pricedLines, linePrices) }
        : {}),
    };
    submitOffer(body);
  };

  if (isLoading || isPending) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  return (
    <AuthLayout width="wide">
      {isError && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("anonymousOffer.error")}
        />
      )}

      {!isError && data && (
        <>
          {data.expired ? (
            data.closesAt || data.finishDate ? (
              <ClosedBiddingNotice
                closesAt={data.closesAt}
                finishDate={data.finishDate}
              />
            ) : (
              <InlineNotification
                kind="warning"
                lowContrast
                hideCloseButton
                title={t("anonymousOffer.expired")}
              />
            )
          ) : (
            <>
              <OrderInfoPanel data={data} />
              <ServicesToQuoteCard
                services={data.serviceToQuote ? [data.serviceToQuote] : []}
              />

              {submitResult ? (
                <Tile className="anonymous-offer__success">
                  {submitResult.updated
                    ? t("anonymousOffer.updated")
                    : t("anonymousOffer.success")}
                </Tile>
              ) : (
                <>
                  {data.invitationUsed && existingOffer && (
                    <InlineNotification
                      kind="info"
                      lowContrast
                      hideCloseButton
                      title={t("anonymousOffer.updateNotice")}
                    />
                  )}
                  {submitError && (
                    <InlineNotification
                      kind="error"
                      lowContrast
                      hideCloseButton
                      title={t(submitError)}
                    />
                  )}
                  <OfferForm
                    control={control}
                    onSubmit={handleSubmit(onSubmit)}
                    isUpdate={Boolean(data.invitationUsed && existingOffer)}
                    buyerPicksUp={Boolean(data.buyerPicksUp)}
                    pricedLines={pricedLines}
                    linePrices={linePrices}
                    onLinePriceChange={changeLinePrice}
                    onLineCurrencyChange={changeLineCurrency}
                    showMissingLinePrices={linePricesTouched}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </AuthLayout>
  );
};

export default AnonymousOffer;
